

import { createContext, useState, useContext, useEffect, useCallback } from 'react';

const MusicPlayerContext = createContext();

export const useMusicPlayer = () => {
    return useContext(MusicPlayerContext);
};

export const MusicPlayerProvider = ({ children }) => {
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audio, setAudio] = useState(null);
    const [progress, setProgress] = useState(0);

    const playTrack = useCallback((track) => {
        if (audio) {
            audio.pause(); // Pause any currently playing audio
            audio.ontimeupdate = null; // Clean up event listeners
            audio.onended = null;
        }

        const newAudio = new Audio(track.url);
        setCurrentTrack(track);
        setAudio(newAudio);
        newAudio.play();
        setIsPlaying(true);

        newAudio.ontimeupdate = () => {
            setProgress((newAudio.currentTime / newAudio.duration) * 100);
        };

        newAudio.onended = () => {
            setIsPlaying(false);
            setProgress(0);
        };
    }, [audio]);

    const pauseTrack = useCallback(() => {
        if (audio) {
            audio.pause();
            setIsPlaying(false);
        }
    }, [audio]);

    const resumeTrack = useCallback(() => {
        if (audio) {
            audio.play();
            setIsPlaying(true);
        }
    }, [audio]);

    const handleVolumeChange = useCallback((value) => {
        if (audio) {
            audio.volume = value / 100;
        }
    }, [audio]);

    // Stop playing track and reset player state
    const handleClose = useCallback(() => {
        if (audio) {
            audio.pause();  // Pause the audio
            audio.currentTime = 0;  // Reset the playback position to the start
            audio.ontimeupdate = null; // Clean up event listeners
            audio.onended = null;
        }
        setCurrentTrack(null);  // Reset current track
        setAudio(null);  // Clear audio object
        setIsPlaying(false);  // Set playing state to false
        setProgress(0);  // Reset progress
    }, [audio]);

    // Seek to a specific time in the track when the progress slider is adjusted
    const seekTrack = useCallback((value) => {
        if (audio) {
            const seekTime = (value / 100) * audio.duration;  // Calculate time to seek to
            audio.currentTime = seekTime;  // Set the audio's current time to the new seek time
            setProgress(value);  // Update the progress state
        }
    }, [audio]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audio) {
                audio.pause();
                audio.ontimeupdate = null;
                audio.onended = null;
            }
        };
    }, [audio]);

    return (
        <MusicPlayerContext.Provider value={{
            currentTrack,
            isPlaying,
            progress,
            playTrack,
            pauseTrack,
            resumeTrack,
            handleVolumeChange,
            handleClose,
            seekTrack,  // Pass seekTrack to the context so it can be used in components
        }}>
            {children}
        </MusicPlayerContext.Provider>
    );
};
